import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Main from '../../components/Main/Main';
import Container from '../../components/Container/Container';
import SEO from '../../components/SEO/SEO';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import SchemaMarkup from '../../components/SchemaMarkup/SchemaMarkup';
import Quote from '../../components/Quote/Quote';
import MainServiceInfo from '../../components/ServicePage/MainServiceInfo';
import TjenesterContent from '../../components/ServicePage/TjenesterContent';
import RelatedBlogs from '../../components/ServicePage/RelatedBlogs';
import ContactCTA from '../../components/Global/ContactCTA/ContactCTA';

import { useIsMobile } from '../../hooks';
import useCalendlyEmbedHeight from '../../hooks/useCalendlyEmbedHeight';
import classNames from 'classnames/bind';
import styles from './[slug].module.scss';
import * as MENUS from '../../constants/menus';
import { useState, useEffect, useMemo } from 'react';
import { createSchema, createServiceSchema, createWebPageSchema, createBreadcrumbSchema } from '../../lib/schema';
import { fetchTjenesteForStaticProps } from '../../lib/fetch-tjeneste-for-static-props';

const cx = classNames.bind(styles);

const SHARED_DATA_QUERY_BASE = gql`
  query GetSharedDataBase(
    $headerLocation: MenuLocationEnum,
    $footerLocation: MenuLocationEnum
  ) {
    generalSettings {
      title
      description
    }
    headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
      nodes {
        id
        label
        path
        parentId
        cssClasses
        menu {
          node {
            name
          }
        }
      }
    }
    footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
      nodes {
        id
        label
        path
        parentId
        cssClasses
        menu {
          node {
            name
          }
        }
      }
    }
    tjenester(first: 100, where: { taxQuery: { taxArray: { terms: "4", field: TAXONOMY_ID } } }) {
      nodes {
        id
        title
        slug
        uri
        terms {
          nodes {
            id
            name
          }
        }
        tjenesterPost {
          serviceDescription
        }
      }
    }
    mediaboosterSettings {
      mediaboosterSettingsFields {
        logo {
          node {
            mediaItemUrl
            altText
          }
        }
        logoIcon {
          node {
            mediaItemUrl
            altText
          }
        }
        ctaSeksjonMotebooking {
          heading
          subtext
        }
        nyhetsbrev {
          tittelNyhetsbrev
          innholdNyhetsbrev
        }
      }
    }
  }
`;

// Fetched separately (and only when a ProductSlider layout exists) so the
// common case stays a single parallel pair of queries instead of a waterfall.
const PRODUCTS_QUERY = gql`
  query GetTjenesteProducts {
    products(first: 50, where: { status: "publish" }) {
      nodes {
        ... on SimpleProduct {
          id
          databaseId
          name
          slug
          onSale
          price
          regularPrice
          salePrice
          stockStatus
          image {
            id
            sourceUrl
            altText
          }
        }
        ... on VariableProduct {
          id
          databaseId
          name
          slug
          onSale
          price
          regularPrice
          salePrice
          stockStatus
          image {
            id
            sourceUrl
            altText
          }
        }
        ... on ExternalProduct {
          id
          databaseId
          name
          slug
          onSale
          price
          regularPrice
          salePrice
          image {
            id
            sourceUrl
            altText
          }
        }
      }
    }
  }
`;

const MAIN_SERVICE_SLUGS_QUERY = gql`
  query GetMainServiceSlugs {
    tjenester(first: 100, where: { taxQuery: { taxArray: { terms: "4", field: TAXONOMY_ID } } }) {
      nodes {
        slug
      }
    }
  }
`;

// Helper function to check if a service is AI-related
function isAIRelatedService(slug) {
  const aiServiceSlugs = [
    'ai-automatisering',
    'automatisering', 
    'kunstig-intelligens',
    'ai-verktoy',
    'ai-verktøy',
    'ai',
    'automation'
  ];
  
  return aiServiceSlugs.some(aiSlug => 
    slug?.toLowerCase().includes(aiSlug.toLowerCase())
  );
}

export async function getStaticPaths() {
  // Prebuild main service paths (the ones linked from the menu) so the very
  // first navigation hits a cached static page. Other services fall back to
  // on-demand blocking generation.
  try {
    const { getApolloClient } = await import('@faustwp/core');
    const apolloClient = getApolloClient();
    const { data } = await apolloClient.query({
      query: MAIN_SERVICE_SLUGS_QUERY,
      fetchPolicy: 'no-cache',
    });

    const paths = (data?.tjenester?.nodes ?? [])
      .map((node) => node?.slug)
      .filter(Boolean)
      .map((slug) => ({ params: { slug } }));

    return { paths, fallback: 'blocking' };
  } catch (error) {
    console.error('Error prebuilding tjeneste paths:', error.message);
    return { paths: [], fallback: 'blocking' };
  }
}

export async function getStaticProps({ params }) {
  const { slug } = params;

  if (process.env.DEBUG_ROUTING === 'true') {
    const { logEnvironmentInfo, logRequestInfo } = await import('../../lib/debug-utils');
    logEnvironmentInfo();
    logRequestInfo({ params }, 'Tjeneste SSG');
  }

  const { getApolloClient } = await import('@faustwp/core');
  const { executeGraphQLWithRetry } = await import('../../lib/graphql-utils');
  const apolloClient = getApolloClient();

  try {
    const uri = `/tjeneste/${slug}/`;

    // Run the service query and shared header/footer query concurrently
    // instead of waterfalling them.
    const [tjenesteResult, sharedResult] = await Promise.all([
      fetchTjenesteForStaticProps(apolloClient, uri),
      executeGraphQLWithRetry(apolloClient, SHARED_DATA_QUERY_BASE, {
        headerLocation: MENUS.PRIMARY_LOCATION,
        footerLocation: MENUS.FOOTER_LOCATION,
      }),
    ]);

    const tjeneste = tjenesteResult.data?.nodeByUri;

    if (!tjeneste) {
      if (process.env.DEBUG_ROUTING === 'true') {
        console.log('Tjeneste not found for URI:', uri);
      }
      return { notFound: true, revalidate: 60 };
    }

    const hasProductSlider = Boolean(
      tjeneste.tjenesterPost?.pageLayout?.some(
        (section) => section?.__typename === 'TjenesterPostPageLayoutProductSliderLayout'
      )
    );

    let sharedData = sharedResult.data;

    // Products are only needed for the ProductSlider layout; fetch them lazily.
    if (hasProductSlider) {
      const productsResult = await executeGraphQLWithRetry(apolloClient, PRODUCTS_QUERY, {});
      sharedData = { ...sharedData, products: productsResult.data?.products };
    }

    return {
      props: {
        initialSlug: slug,
        tjenesteData: tjenesteResult.data,
        sharedData,
        ...(tjenesteResult.source === 'dev-auth' ? { isDevPreview: true } : {}),
      },
      revalidate: tjenesteResult.source === 'dev-auth' ? 1 : 300,
    };
  } catch (error) {
    console.error('Error fetching tjeneste data:', error);
    console.error('Full error details:', error.message);
    return { notFound: true, revalidate: 60 };
  }
}

export default function SingleTjeneste({ initialSlug, tjenesteData, sharedData }) {
  const router = useRouter();
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Use server-side rendered data directly
  const tjeneste = tjenesteData?.nodeByUri || {};
  const { title, content, featuredImage, seo, tjenesterPost, children } = tjeneste;
  const calendlyEmbed = useCalendlyEmbedHeight(mounted ? tjenesterPost?.buttonUrl : null);
  const { title: siteTitle, description: siteDescription } = sharedData?.generalSettings ?? {};
  const primaryMenu = sharedData?.headerMenuItems?.nodes ?? [];
  const footerMenu = sharedData?.footerMenuItems?.nodes ?? [];
  const lightLogo = sharedData?.mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;
  const products = sharedData?.products?.nodes ?? [];

  // Extract pageLayout data for TjenesterContent
  const pageLayout = tjenesterPost?.pageLayout;
  // SEO values with comprehensive social media metadata
  const pageTitle = seo?.title || title || 'Tjeneste';
  const pageDescription = seo?.description || content || `Profesjonelle ${title} tjenester hos Mediabooster`;
  const pageImage = seo?.openGraph?.image?.secureUrl || featuredImage?.node?.mediaItemUrl || 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png';

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: 'Hjem', path: '/' },
    { label: 'Tjenester', path: '/tjenester' },
    { label: title, path: router.asPath }
  ];

  // Check if EntryHeaderSlider should be rendered - ensure we have valid images
  const hasConnectedProjects = tjenesterPost?.connectedProjects?.nodes?.filter(slide => 
    slide?.featuredImage?.node?.mediaItemUrl && 
    slide?.featuredImage?.node?.mediaItemUrl.trim() !== ''
  ).length > 0;

  // Generate Service schema for structured data
  const serviceSchema = useMemo(() => {
    if (!title) return null;
    
    return createSchema([
      createWebPageSchema({
        title: pageTitle,
        description: pageDescription,
        url: tjeneste?.uri,
        imageUrl: pageImage
      }),
      createServiceSchema({
        name: title,
        description: tjenesterPost?.serviceDescription || pageDescription,
        url: tjeneste?.uri,
        imageUrl: pageImage
      }),
      createBreadcrumbSchema(breadcrumbItems)
    ]);
  }, [title, pageTitle, pageDescription, tjeneste?.uri, pageImage, tjenesterPost?.serviceDescription, breadcrumbItems]);

  return (
    <>
      <SEO 
        seo={{
          title: pageTitle,
          description: pageDescription,
          canonicalUrl: seo?.canonicalUrl || tjeneste?.uri,
          robots: seo?.robots,
          openGraph: {
            title: pageTitle,
            description: pageDescription,
            url: seo?.canonicalUrl || tjeneste?.uri,
            siteName: siteTitle || 'Mediabooster',
            type: 'article',
            image: {
              url: pageImage,
              secureUrl: pageImage,
              width: 1200,
              height: 630,
              alt: `${title} - Mediabooster tjeneste`
            }
          },
          twitter: {
            card: 'summary_large_image',
            site: '@mediabooster',
            title: pageTitle,
            description: pageDescription,
            image: pageImage
          }
        }}
      />
      <SchemaMarkup schema={serviceSchema} />
      
      <Header
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        lightLogo={lightLogo}
        sharedData={sharedData}
        hasHeroSlider={false}
        />
      
      <Main>
        <Container>
          <div className={cx('container', { 'hasSlider': hasConnectedProjects, 'noSlider': !hasConnectedProjects })}>
            <MainServiceInfo 
              title={title}
              description={content}
              tjenesterPost={tjenesterPost}
              children={children}
              breadcrumbItems={breadcrumbItems}
            />
            

            {/* TjenesterContent component with layout data */}
            {pageLayout && (
              <TjenesterContent 
                layout={pageLayout} 
                products={products} 
                tjenesteId={tjeneste.databaseId}
                schemaOptions={{
                  pageUrl: tjeneste?.uri,
                  pageTitle: pageTitle
                }}
              />
            )}
            
            {/* Quote section - render if quote exists */}
            {tjenesterPost?.quote && (
              <Quote text={tjenesterPost.quote} />
            )}
            
            {/* Iframe section - render if buttonUrl exists (same ACF field as primary button) */}
            {calendlyEmbed.src && (
              <div className={cx('iframeSection')}>
                <h2 className={cx('iframeTitle')}>Book et introduksjonsmøte med oss</h2>
                <iframe
                  ref={calendlyEmbed.ref}
                  src={calendlyEmbed.src}
                  width="100%"
                  height={calendlyEmbed.height || 800}
                  frameBorder="0"
                  scrolling="no"
                  title="Booking"
                  style={{
                    border: 'none',
                    marginBottom: '2rem',
                    ...(calendlyEmbed.height
                      ? { height: calendlyEmbed.height, minHeight: calendlyEmbed.height }
                      : null),
                  }}
                />
              </div>
            )}
            
            <RelatedBlogs connectedBlogPosts={tjenesterPost?.connectedBlogPosts || { nodes: [] }} />
            
            {/* <ContactCTA sharedData={sharedData} /> */}
          </div>
        </Container>
      </Main>
      
      <Footer
        title={siteTitle}
        menuItems={footerMenu}
        sharedData={sharedData}
      />
    </>
  );
} 